<script setup lang="ts">
import { watch, computed, reactive } from 'vue'
import { marked } from 'marked'
import { useStrapi } from '#imports'
import { generateBlogPostPageMeta } from '~/utils/meta/generateBlogPageMeta'

definePageMeta({
  layout: 'marketing'
})
const route = useRoute()
const router = useRouter()
const { findOne } = useStrapi()
const { data: articleData, refresh } = await useAsyncData(route.fullPath, () =>
  findOne('articles', {
    filters: {
      slug: { $eq: route.params.slug },
      site: { $eq: 'employers.io' }
    },
    populate: {
      cover: true,
      author: true,
      category: true,
      blocks: {
        populate: '*'
      }
    }
  })
)
watch(
  () => route.fullPath,
  async () => {
    await refresh()
  },
  { immediate: true }
)
const isArticleNotFound = computed(() => {
  return !articleData.value?.data
    || !Array.isArray(articleData.value.data)
    || articleData.value.data.length === 0
})

// Set 404 status code for SSR
if (isArticleNotFound.value) {
  const event = useRequestEvent()
  if (event) {
    setResponseStatus(event, 404)
  }
}
const article = computed(() => articleData.value?.data[0])
generateBlogPostPageMeta(route, article.value)
const formatDate = (date: string) => {
  const d = new Date(date)
  return new Date(
    d.getTime() - d.getTimezoneOffset() * 60000
  ).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC'
  })
}
const parseContent = (content: string) => {
  return marked(content)
}
const accordionOpen = reactive<Record<string, boolean>>({})
const toggleAccordion = (id: string) => {
  accordionOpen[id] = !accordionOpen[id]
}
if (import.meta.server && article.value?.blocks) {
  const jsonSchemas: string[] = []
  article.value.blocks.forEach((block: any, index: number) => {
    if (
      block.__component === 'shared.json-schema'
      && block.isActive
      && block.schemaContent
    ) {
      try {
        let parsedSchema
        if (
          typeof block.schemaContent === 'object'
          && block.schemaContent !== null
        ) {
          parsedSchema = block.schemaContent
        } else if (typeof block.schemaContent === 'string') {
          parsedSchema = JSON.parse(block.schemaContent)
        } else {
          return
        }
        if (parsedSchema['@context'] && parsedSchema['@type']) {
          const schemaString = JSON.stringify(parsedSchema)
          jsonSchemas.push(schemaString)
        }
      } catch (error) {}
    }
  })
  if (jsonSchemas.length > 0) {
    useHead({
      script: jsonSchemas.map((schema, index) => ({
        type: 'application/ld+json',
        innerHTML: schema,
        key: `blog-schema-${index}`
      }))
    })
  }
}
</script>

<template>
  <ErrorsErrorPage
    v-if="isArticleNotFound"
    :status-code="404"
    title="Article not found."
    subtitle="The article you're looking for doesn't exist or has been moved."
  />
  <main
    v-else-if="article"
    class="min-h-screen bg-white"
  >
    <div class="relative pt-64 h-96 lg:min-h-[370px] bg-gray-900">
      <img
        v-if="article.cover?.formats?.large?.url"
        :src="article.cover.formats.large.url"
        :alt="article.cover.alternativeText"
        width="1200"
        height="370"
        class="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-75"
        loading="eager"
        decoding="async"
      >
      <div
        class="absolute inset-0 bg-gradient-to-b from-gray-900/70 to-gray-900/50"
      />
      <div class="relative h-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col justify-end h-full pb-8">
          <div
            v-if="article.category"
            class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-600 text-white mb-4 w-fit"
          >
            {{ article.category.name }}
          </div>
          <h1
            class="text-4xl md:text-5xl lg:text-6xl font-bold text-white max-w-4xl mb-6"
          >
            {{ article.title }}
          </h1>
          <div class="flex items-center text-gray-300 space-x-4">
            <div
              v-if="article.author"
              class="flex items-center"
            >
              <span class="font-medium">{{ article.author.name }}</span>
            </div>
            <span>•</span>
            <time>{{ formatDate(article.publishedAt) }}</time>
          </div>
        </div>
      </div>
    </div>
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="mt-12 mb-10">
        <p class="text-xl md:text-2xl text-gray-600 leading-relaxed">
          {{ article.description }}
        </p>
      </div>
      <article class="prose prose-lg max-w-none mb-16">
        <template
          v-for="(block, index) in article.blocks"
          :key="`block-${index}`"
        >
          <div
            v-if="block.__component === 'shared.rich-text'"
            class="mb-8"
            v-html="parseContent(block.body)"
          />
          <blockquote
            v-else-if="block.__component === 'shared.quote'"
            class="not-prose my-8 pl-6 border-l-4 border-indigo-500 italic text-gray-700"
          >
            {{ block.quote }}
          </blockquote>
          <div
            v-else-if="block.__component === 'shared.button'"
            class="not-prose my-8"
          >
            <component
              :is="block.url ? 'a' : 'button'"
              :href="block.url || undefined"
              :target="block.openInNewTab ? '_blank' : undefined"
              :class="[
                'inline-flex items-center px-6 py-3 rounded-full font-medium transition-colors',
                block.variant === 'search'
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : block.variant === 'secondary'
                    ? 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                    : 'bg-green-600 text-white hover:bg-green-700',
                block.size === 'small'
                  ? 'px-4 py-2 text-sm'
                  : block.size === 'large'
                    ? 'px-8 py-4 text-lg'
                    : 'px-6 py-3'
              ]"
            >
              {{ block.text }}
            </component>
          </div>
          <div
            v-else-if="block.__component === 'shared.info'"
            :class="[
              'not-prose my-8 p-4 rounded-lg border-l-4 flex items-start space-x-3',
              block.variant === 'warning'
                ? 'bg-yellow-50 border-yellow-400'
                : block.variant === 'tip'
                  ? 'bg-green-50 border-green-400'
                  : block.variant === 'error'
                    ? 'bg-red-50 border-red-400'
                    : block.variant === 'success'
                      ? 'bg-green-50 border-green-400'
                      : 'bg-blue-50 border-blue-400'
            ]"
          >
            <div
              v-if="block.showIcon"
              class="flex-shrink-0 mt-1"
            >
              <svg
                v-if="block.variant === 'warning'"
                class="w-5 h-5 text-yellow-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
              <svg
                v-else-if="
                  block.variant === 'tip' || block.variant === 'success'
                "
                class="w-5 h-5 text-green-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <svg
                v-else-if="block.variant === 'error'"
                class="w-5 h-5 text-red-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                />
              </svg>
              <svg
                v-else
                class="w-5 h-5 text-blue-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="flex-1">
              <h4
                v-if="block.title"
                class="font-medium text-gray-900 mb-1"
              >
                {{ block.title }}
              </h4>
              <div
                class="text-gray-700"
                v-html="parseContent(block.content)"
              />
            </div>
          </div>
          <div
            v-else-if="block.__component === 'shared.feature-list'"
            class="not-prose my-8"
          >
            <ul class="space-y-2">
              <li
                v-for="item in block.items"
                :key="item.id"
                class="flex items-center space-x-3"
              >
                <svg
                  v-if="item.status === 'available'"
                  class="w-5 h-5 text-green-500 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <svg
                  v-else
                  class="w-5 h-5 text-red-500 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="text-gray-700">{{ item.text }}</span>
              </li>
            </ul>
          </div>
          <div
            v-else-if="block.__component === 'shared.code'"
            class="not-prose my-8"
          >
            <h4
              v-if="block.title"
              class="font-medium text-gray-900 mb-4"
            >
              {{ block.title }}
            </h4>
            <div class="bg-gray-50 rounded-lg p-4 overflow-x-auto">
              <div v-html="block.htmlContent" />
            </div>
          </div>
          <div
            v-else-if="block.__component === 'shared.media'"
            class="not-prose my-8"
          >
            <div class="aspect-video relative overflow-hidden rounded-lg shadow-lg">
              <img
                v-if="block.file?.url && !block.file.mime?.startsWith('video/')"
                :src="block.file.url"
                :alt="block.file.alternativeText || ''"
                :width="block.file.width || 800"
                :height="block.file.height || 450"
                class="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              >
              <video
                v-else-if="block.file?.url && block.file.mime?.startsWith('video/')"
                :src="block.file.url"
                controls
                class="w-full h-full object-cover"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
          <div
            v-else-if="block.__component === 'shared.accordion'"
            class="not-prose my-8"
          >
            <div class="space-y-2">
              <div
                v-for="item in block.items"
                :key="item.id"
                class="border border-gray-200 rounded-lg"
              >
                <button
                  class="w-full px-4 py-3 text-left font-medium text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center justify-between"
                  @click="toggleAccordion(item.id)"
                >
                  <span>{{ item.title }}</span>
                  <svg
                    class="w-5 h-5 transition-transform"
                    :class="{ 'rotate-180': accordionOpen[item.id] }"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
                <div
                  v-show="accordionOpen[item.id] || item.defaultOpen"
                  class="px-4 pb-4"
                >
                  <div
                    class="text-gray-700"
                    v-html="parseContent(item.content)"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            v-else-if="block.__component === 'shared.columns'"
            class="not-prose my-8"
          >
            <div
              :class="[
                'grid',
                block.columnCount === '2'
                  ? 'grid-cols-1 md:grid-cols-2'
                  : block.columnCount === '3'
                    ? 'grid-cols-1 md:grid-cols-3'
                    : 'grid-cols-1 md:grid-cols-4',
                block.gap === 'small'
                  ? 'gap-4'
                  : block.gap === 'large'
                    ? 'gap-12'
                    : 'gap-8'
              ]"
            >
              <div
                v-for="column in block.columns"
                :key="column.id"
                :class="[
                  column.width === 'full'
                    ? 'col-span-full'
                    : column.width === '1/2'
                      ? 'md:col-span-2'
                      : column.width === '1/3'
                        ? 'md:col-span-1'
                        : column.width === '2/3'
                          ? 'md:col-span-2'
                          : column.width === '1/4'
                            ? 'md:col-span-1'
                            : column.width === '3/4'
                              ? 'md:col-span-3'
                              : ''
                ]"
              >
                <div
                  v-if="column.content"
                  class="prose prose-lg max-w-none"
                  v-html="parseContent(column.content)"
                />
              </div>
            </div>
          </div>
        </template>
      </article>
      <footer class="border-t border-gray-200 pt-8 pb-16">
        <div class="flex items-center justify-between">
          <div v-if="article.author">
            <h3 class="text-lg font-semibold text-gray-900">
              About {{ article.author.name }}
            </h3>
            <p class="text-gray-600 mt-1">
              {{ article.author.description }}
            </p>
            <p class="text-gray-600 mt-1">
              {{ article.author.email }}
            </p>
          </div>
        </div>
      </footer>
    </div>
  </main>
</template>

<style>
.prose {
  @apply prose-headings:text-gray-900 prose-headings:font-semibold prose-headings:tracking-tight;
  @apply prose-p:text-gray-600 prose-p:leading-relaxed;
  @apply prose-a:text-indigo-600 prose-a:no-underline hover:prose-a:underline;
  @apply prose-strong:text-gray-900 prose-strong:font-semibold;
  @apply prose-ul:list-disc prose-ul:text-gray-600;
  @apply prose-ol:text-gray-600;
  @apply prose-blockquote:text-gray-500 prose-blockquote:border-indigo-500;
  @apply prose-img:rounded-lg prose-img:shadow-lg;
  @apply prose-h2:text-3xl prose-h2:mt-16 prose-h2:mb-8;
  @apply prose-hr:border-gray-200;
}
.prose h4,
h4 {
  @apply text-xl font-semibold text-gray-900;
}
.grid .prose h3 {
  @apply text-lg font-semibold text-gray-900 mt-0 mb-3;
}
.grid .prose ul {
  @apply list-disc pl-6 mb-4 text-gray-600;
}
.grid .prose li {
  @apply mb-1;
}
html {
  scroll-behavior: smooth;
}
</style>
